import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useInsertionEffect, useLayoutEffect } from 'react';
import type { MagneticWallHighlight } from '../editor/editorSlice';

const visibleItemIdsAtom = atom(new Set<string>());

export const useVisibleMagneticWallItemIds = () =>
  useAtomValue(visibleItemIdsAtom);

export const useIsMagneticWallItemVisible = () => {
  return useAtomCallback(
    useCallback((get, _set, id: string) => get(visibleItemIdsAtom).has(id), []),
  );
};

export const MAGNETIC_WALL_ITEMS_DEFAULT_STYLES = `
  [class*='annotation-'] {
    --color-annotation-highlight-default: var(--color-editor-annotation-bg-default);
    --color-annotation-highlight-emphasis: var(--color-editor-annotation-bg-emphasis);
  }

  [class*='comment-'] {
    --color-comment-highlight-default: var(--color-editor-comment-bg-default);
    --color-comment-highlight-emphasis: var(--color-editor-comment-bg-emphasis);
  }

  [class*='reaction-'] {
    --color-reaction-highlight-default: var(--color-editor-reaction-bg-default);
    --color-reaction-highlight-emphasis: var(--color-editor-reaction-bg-emphasis);
  }
`;

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export function getCssForHiddenItems(items: MagneticWallHighlight[]) {
  const selector = (['comment', 'reaction', 'annotation'] as const)
    .map((type) => {
      const idsForType = items.filter((item) => item.type === type);
      if (idsForType.length === 0) return `[class*='${type}']`;

      const classList = idsForType
        .map((item) => `.${item.type}-${item.id}`)
        .join(',');
      return `[class*='${type}']:not(${classList})`;
    })
    .join(',');

  if (selector === '') return '';

  return `${selector} {
    --color-annotation-highlight-default: transparent;
    --color-annotation-highlight-emphasis: transparent;
    --color-comment-highlight-default: transparent;
    --color-comment-highlight-emphasis: transparent;
    --color-reaction-highlight-default: transparent;
    --color-reaction-highlight-emphasis: transparent;
  }`;
}

const useVisibleMagneticWallItems = (items: MagneticWallHighlight[]) => {
  const setVisibleItemIds = useSetAtom(visibleItemIdsAtom);
  const sortedItems: MagneticWallHighlight[] = items.toSorted((a, b) =>
    a.id.localeCompare(b.id),
  );
  const stringifiedItems = JSON.stringify(
    sortedItems.map((item) => ({ id: item.id, type: item.type })),
  );
  useLayoutEffect(() => {
    const visibleItemList = JSON.parse(
      stringifiedItems,
    ) as MagneticWallHighlight[];
    const visibleItemIds = new Set(visibleItemList.map((item) => item.id));
    setVisibleItemIds(visibleItemIds);
  }, [setVisibleItemIds, stringifiedItems]);

  useInsertionEffect(() => {
    const visibleItemList = JSON.parse(
      stringifiedItems,
    ) as MagneticWallHighlight[];
    if (!visibleItemList.length) return undefined;

    const styleTag = document.createElement('style');
    document.head.appendChild(styleTag);

    styleTag.textContent = `
      ${MAGNETIC_WALL_ITEMS_DEFAULT_STYLES}
      ${getCssForHiddenItems(visibleItemList)}
    `;

    return () => {
      styleTag.remove();
    };
  }, [stringifiedItems]);
};

export default useVisibleMagneticWallItems;
